import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { Applicant, Consumer } from "../../flatRates/types";
import type { RegistrationParams } from "../../flatRates/types";
import InfoDisplay from "@/components/InfoDisplay";
import Layout from "../../shared/components/Layout";
import { formatDate } from '@/utils/format';
import { useFlatRateContext } from "../../shared/providers/FlatRateProvider";
import { AddressFieldBox } from "@/components/AddressField";
import { PhoneFieldBox } from "@/components/PhoneField";
import OccupationLabels from "../../shared/types/OccupationLabels";
import SexLabels from "../../shared/types/SexLabels";
import createEditInfo from "../api/createEditInfo";
import clearLocalStorage from "@/utils/clearLocalStorage";

const EditInfoConfirm: React.FC = () => {

  const OccupationLabels: OccupationLabels = {
    employee: "会社員・公務員",
    independent: "自営",
    housewife: "主婦",
    student: "学生",
    other: "その他"
  }

  const SexLabels: SexLabels = {
    male: "男性",
    female: "女性",
    unknown: "不明",
  }

  const navigate = useNavigate();

  const { passwordValues, setAlert } = useFlatRateContext();

  const [customer, setCustomer] = useState({
    custCode: "",
    email: "",
  });

  const [applicant, setApplicant] = useState<Applicant>({
    address1: "",
    address2: "",
    birthday: "",
    homePhone: "",
    mobilePhone: "",
    name: "",
    nameKana: "",
    occupation: "",
    sex: "",
    stateId: "",
    zipcode: "",
  });
  const [consumer, setConsumer] = useState<Consumer>({
    address1: "",
    address2: "",
    birthday: "",
    homePhone: "",
    mobilePhone: "",
    name: "",
    nameKana: "",
    occupation: "",
    relationship: "",
    sex: "",
    stateId: "",
    zipcode: "",
    isSameApplicant: true,
    isSameAddress: true,
    isSamePhone: true
  });
  
  const [states, setStates] = useState([]);

  useEffect(() => {
      const storedCustomer = localStorage.getItem("customer");
      if(storedCustomer) {
        setCustomer(JSON.parse(storedCustomer));
      }

      const storedApplicant = localStorage.getItem("applicant");
      if(storedApplicant) {
        setApplicant(JSON.parse(storedApplicant));
      }

      const storedConsumer = localStorage.getItem("consumer");
      if(storedConsumer) {
        setConsumer(JSON.parse(storedConsumer));
      }
  
      const storedStates = localStorage.getItem("states");
      if(storedStates) {
        setStates(JSON.parse(storedStates));
      }
  }, []);

  const applicantAddressData = {
    zipcode: {
      label: "郵便番号",
      value: applicant.zipcode,
      onChange: undefined
    },
    stateId: {
      label: "都道府県",
      value: applicant.stateId ? applicant.stateId.toString() : "",
      onChange: undefined
    },
    address1: {
      label: "市区町村",
      placeholder: "",
      value: applicant.address1,
      onChange: undefined
    },
    address2: {
      label: "建物名",
      placeholder: "",
      value: applicant.address2,
      onChange: undefined
    },
  }

  const consumerAddressData = {
    zipcode: {
      label: "郵便番号",
      value: consumer.zipcode,
      onChange: undefined
    },
    stateId: {
      label: "都道府県",
      value: consumer.stateId ? consumer.stateId.toString() : "",
      onChange: undefined
    },
    address1: {
      label: "市区町村",
      placeholder: "",
      value: consumer.address1,
      onChange: undefined
    },
    address2: {
      label: "建物名",
      placeholder: "",
      value: consumer.address2,
      onChange: undefined
    },
  }

  const handleSubmit = async() => {
    const params: RegistrationParams = {
      customer: {...customer, ...passwordValues},
      applicant: applicant,
      consumer: consumer,
    }

    try {
      const res = await createEditInfo(params);
      if(res.status === 201) {
        clearLocalStorage();
        navigate("/customer/edit_info/complete");
      } else if(res.status !== 201) {
        navigate(`/customer/edit_info`);
        window.scrollTo(0, 0);
        console.error(`status: ${res.status}, message: ${res.statusText}`);
      }
    } catch (err: any) {
      navigate(`/customer/edit_info`);
      window.scrollTo(0, 0);
      console.log('An unexpected error occurred:', err.message);
    }
  }

  const renderPreviousPage = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    navigate("/customer/edit_info", { state: { fromConfirmPage: true } });
    window.scrollTo(0, 0);
  }

  const passwordValidate = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setAlert({open: true, messages: ['パスワードを再入力してください。']});
    navigate("/customer/edit_info");
    window.scrollTo(0, 0);
  }

  return(
    <Layout>
      <main id="page-user_index" className="wrapper">
        <section className="sec-contact">
          <div className="inner">
            <h1 className="c-ttl__01">お客様基本情報入力確認</h1>
            <div className="contact-box">
              <div className="contact-box__left">
                <div className="contact-box__form js-item" id="id-01">
                  <h2 className="c-ttl__02">お客様情報</h2>
                  <InfoDisplay label={"お客様ID"} value={customer.custCode} />
                  <InfoDisplay label={"メールアドレス"} value={customer.email} />
                  <InfoDisplay label={"パスワード"} value={passwordValues.password ? "*********" : ""} />
                  <InfoDisplay label={"パスワード確認"} value={passwordValues.passwordConfirmation ? "*********" : ""} />
                </div>
                <div className="contact-box__form mbsp-29 js-item h-adr" id="id-02">
                  <h2 className="c-ttl__02">申込者情報</h2>
                  <InfoDisplay label="申込者名(カナ)" value={applicant.nameKana} />
                  <InfoDisplay label="申込者名(漢字)" value={applicant.name} />
                  <InfoDisplay label="申込者性別" value={applicant.sex ? SexLabels[applicant.sex] : ""} />
                  <InfoDisplay label="申込者生年月日" value={formatDate(applicant.birthday)} />
                  <AddressFieldBox
                    label="申込者住所"
                    addressData={applicantAddressData}
                    required={false}
                    states={states}
                    inputAddressData={undefined}
                  />
                  <PhoneFieldBox
                    label="申込者電話番号"
                    phoneData={{
                      homePhone: {label: "自宅", value: applicant.homePhone, onChange: undefined},
                      mobilePhone: {label: "携帯", value: applicant.mobilePhone, onChange: undefined},
                    }}
                    required={false}
                  />
                  <InfoDisplay label="申込者ご職業" value={applicant.occupation ? OccupationLabels[applicant.occupation] : ""} />
                </div>
                <div className="contact-box__form mbsp-29 js-item h-adr" id="id-03">
                  <h2 className="c-ttl__02">レンズ使用者情報</h2>
                  <InfoDisplay label="使用者について" value={consumer.isSameApplicant ? "申込者と同一" : "申込者と異なる"} />
                  { !consumer.isSameApplicant &&
                    <>
                      <InfoDisplay label="使用者名(カナ)" value={consumer.nameKana} />
                      <InfoDisplay label="使用者名(漢字)" value={consumer.name} />
                      <InfoDisplay label="申込者との関係" value={consumer.relationship} />
                      <InfoDisplay label="使用者性別" value={consumer.sex ? SexLabels[consumer.sex] : ""} />
                      <InfoDisplay label="使用者生年月日" value={formatDate(consumer.birthday)} />
                      { consumer.isSameAddress ?
                        <InfoDisplay label="使用者住所" value={"申込者と同一"} />
                      :
                        <AddressFieldBox
                          label="使用者住所"
                          addressData={consumerAddressData}
                          required={false}
                          states={states}
                          inputAddressData={undefined}
                        />
                      }

                      { consumer.isSamePhone ?
                        <InfoDisplay label="使用者電話番号" value={"申込者と同一"} />
                      :
                        <PhoneFieldBox
                          label="使用者電話番号"
                          phoneData={{
                            homePhone: {label: "自宅", value: consumer.homePhone, onChange: undefined},
                            mobilePhone: {label: "携帯", value: consumer.mobilePhone, onChange: undefined},
                          }}
                          required={false}
                        />
                      }
                      <InfoDisplay label="申込者ご職業" value={consumer.occupation ? OccupationLabels[consumer.occupation] : ""} />
                    </>
                  }
                </div>
                {(passwordValues.password === '' && passwordValues.passwordConfirmation === '') ?
                  <button className="c-btn__01" onClick={passwordValidate}>確定</button>
                  :
                  <button className="c-btn__01" onClick={handleSubmit}>確定</button>
                }
                
                <div className="c-al--c">
                  <button className="c-btn__02" onClick={renderPreviousPage}>前のページに戻る</button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  )
};

export default EditInfoConfirm;